import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Package, Clock, Plus, LayoutDashboard, History, ArrowUp, CheckCircle,
  Navigation, Settings, DollarSign, MapPin, Calendar, Loader2, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Progress } from '@/components/ui/progress';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Bar, BarChart } from 'recharts';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import useAuthStore from '@/store/authStore';
import { useClientStats } from '@/hooks/useClientStats';
import { useClientRequests } from '@/hooks/useClientRequests';
import { useClientPayments } from '@/hooks/useClientPayments';
import { getClientSidebarLinks } from '@/config/clientSidebarLinks';

const ClientDashboard = () => {
  const { user } = useAuthStore();
  const { stats, isLoading: statsLoading, fetchStats } = useClientStats();
  const { requests, loading: requestsLoading } = useClientRequests({ status: 'all' });
  const { payments } = useClientPayments({});
  
  useEffect(() => {
    if (statsLoading && !stats) {
      fetchStats();
    }
  }, [statsLoading, stats, fetchStats]);
  
  const sidebarLinks = getClientSidebarLinks();

  // Calculer les statistiques à partir des données réelles
  const dashboardStats = useMemo(() => {
    const totalSpent = payments
      .filter(p => p.status === 'SUCCESS')
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    
    const pendingRequests = requests.filter(r => 
      r.status === 'DRAFT' || r.status === 'PUBLISHED' || r.status === 'ASSIGNED'
    ).length;
    
    const completedRequests = requests.filter(r => r.status === 'COMPLETED').length;
    const cancelledRequests = requests.filter(r => r.status === 'CANCELLED').length;
    
    // Calculer la satisfaction moyenne (à partir des ratings si disponibles)
    const satisfaction = stats?.rating_avg ? (parseFloat(stats.rating_avg) * 20) : 92; // Convertir de 0-5 à 0-100
    
    return {
      totalSpent,
      pendingRequests,
      completedRequests,
      cancelledRequests,
      satisfaction,
    };
  }, [payments, requests, stats]);

  // Générer des données de graphique à partir des paiements mensuels
  const chartData = useMemo(() => {
    const monthlyPayments = {};
    payments
      .filter(p => p.status === 'SUCCESS')
      .forEach(payment => {
        const date = new Date(payment.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthName = date.toLocaleDateString('fr-FR', { month: 'short' });
        if (!monthlyPayments[monthKey]) {
          monthlyPayments[monthKey] = { name: monthName, dépenses: 0 };
        }
        monthlyPayments[monthKey].dépenses += parseFloat(payment.amount) || 0;
      });
    
    return Object.values(monthlyPayments).slice(-6); // Derniers 6 mois
  }, [payments]);

  // Demander les demandes récentes
  const recentRequests = useMemo(() => {
    return requests
      .filter(r => r.status !== 'CANCELLED')
      .slice(0, 3)
      .map(request => {
        let progress = 0;
        let statusLabel = 'Brouillon';
        
        if (request.status === 'DRAFT') {
          progress = 10;
          statusLabel = 'Brouillon';
        } else if (request.status === 'PUBLISHED') {
          progress = 25;
          statusLabel = 'Publiée';
        } else if (request.status === 'ASSIGNED') {
          progress = 50;
          statusLabel = 'Assignée';
        } else if (request.status === 'IN_TRANSIT') {
          progress = 75;
          statusLabel = 'En transit';
        } else if (request.status === 'COMPLETED') {
          progress = 100;
          statusLabel = 'Terminée';
        }
        
        return {
          ...request,
          progress,
          statusLabel,
        };
      });
  }, [requests]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 GNF';
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M GNF`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K GNF`;
    }
    return `${amount?.toFixed(0) || 0} GNF`;
  };

  return (
    <>
      <Helmet>
        <title>Tableau de bord Client - LogiConnect</title>
        <meta name="description" content="Gérez vos demandes de transport et suivez vos livraisons" />
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Aperçu Client</h1>
            {(user?.logiconnect_id || user?.profile_data?.logiconnect_id) && (
              <div className="mt-2">
                <LogiConnectIdBadge 
                  id={user?.profile_data?.logiconnect_id || user?.logiconnect_id} 
                  showLabel={true}
                  size="sm"
                />
              </div>
            )}
          </div>
          <Button asChild className="gradient-secondary text-primary font-semibold w-full sm:w-auto">
            <Link to="/client/requests">
              <Plus className="w-5 h-5 mr-2" />
              Nouvelle demande
            </Link>
          </Button>
        </div>

        {/* Statistiques */}
        {statsLoading ? (
          <div className="flex items-center justify-center h-64 mb-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Dépenses totales</p>
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  {formatCurrency(dashboardStats.totalSpent)}
                </p>
                <div className="flex items-center text-sm text-green-500">
                  <ArrowUp className="w-4 h-4 mr-1"/>
                  <span>Toutes les transactions</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Demandes en cours</p>
                  <Package className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-3xl lg:text-4xl font-bold text-foreground">
                  {dashboardStats.pendingRequests}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Demandes terminées</p>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-3xl lg:text-4xl font-bold text-foreground">
                  {dashboardStats.completedRequests}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors flex flex-col justify-between">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground mb-2">Satisfaction</p>
                <div className="flex items-center justify-center my-auto">
                  <div className="relative w-24 h-24">
                    <svg className="w-full h-full" viewBox="0 0 36 36">
                      <path
                        className="text-primary-600"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        strokeWidth="3"
                      />
                      <path
                        className="text-secondary"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                        fill="none"
                        strokeWidth="3"
                        strokeDasharray={`${dashboardStats.satisfaction}, 100`}
                        strokeDashoffset="0"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-foreground">
                        {Math.round(dashboardStats.satisfaction)}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 bg-card/50 border-border">
            <CardContent className="p-6">
              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-foreground">Suivi des Dépenses</h2>
              </div>
              {chartData.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucune donnée de dépenses disponible</p>
                  </div>
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#E3B23C" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#E3B23C" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${value/1000000}M`} />
                      <Tooltip contentStyle={{backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))'}}/>
                      <Area type="monotone" dataKey="dépenses" stroke="#E3B23C" fillOpacity={1} fill="url(#colorUv)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-6">
              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-foreground">Statut des demandes</h2>
              </div>
              {requestsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : recentRequests.length === 0 ? (
                <div className="space-y-4 text-center py-8 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune demande récente</p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/client/requests">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer une demande
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {recentRequests.map((request) => (
                    <div key={request.id}>
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-primary-600/20 flex items-center justify-center">
                            <Package className="w-5 h-5 text-secondary"/>
                          </div>
                          <span className="text-foreground font-medium">
                            #{request.id.slice(-8)}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {request.statusLabel}
                        </Badge>
                      </div>
                      <div className="mb-2 text-xs text-muted-foreground">
                        {request.from_location?.name || 'N/A'} → {request.to_location?.name || 'N/A'}
                      </div>
                      <Progress value={request.progress} className="h-2" />
                    </div>
                  ))}
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <Link to="/client/requests">
                      Voir toutes les demandes
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default ClientDashboard;
